import React from 'react';
import PropTypes from 'prop-types';
import makeStyles from '@mui/styles/makeStyles';
import TextField from '@mui/material/TextField';
import Box from '@mui/material/Box';
import { Helmet } from 'react-helmet';
import appConfig from '@/config';
import CommonTheme from '../../../Theme';
import * as validateRule from '../../validateRule';

/**
 * 共通部分スタイル定義。
 */
const useStyles = makeStyles((theme) => ({
  root: {
    'textAlign': 'center',
    'flexDirection': 'column',
    'width': '95%',
    'padding': '16px 2px',
    'margin': '0 auto',

    // 1366x768と以下
    '@media (max-width: 1366px), (max-resolution: 1.3dppx)': {
      marginTop: '20px',
    },

    // 1367x769～1920x1080
    '@media (min-width: 1367px) and (max-width: 1920px), (min-resolution: 1.5dppx) and (max-resolution: 2dppx)': {
      width: '80%',
      marginTop: '60px',
    },

    // 1921x1081～2560x1440
    '@media (min-width: 1921px) and (max-width: 2560px), (min-resolution: 2.1dppx) and (max-resolution: 3dppx)': {
      width: '60%',
      marginTop: '80px',
    },

    // 2561x1441以上
    '@media (min-width: 2561px), (min-resolution: 3.1dppx)': {
      width: '40%',
      marginTop: '100px',
    },
  },
  box: {
    background: '#fff',
    boxShadow: '0 0 10px #333',
    maxWidth: theme.breakpoints.values.sm,
    marginLeft: 'auto',
    marginRight: 'auto',
  },
  title: {
    fontSize: '1.5em',
    margin: '0px 20px 16px 20px',
    textAlign: 'center',
    fontWeight: 'bold',
    color: CommonTheme.palette.primary.contrastText,
    backgroundColor: CommonTheme.logo.main,
    padding: '10px 20px',
    position: 'relative',
  },
}));

/**
 * 子要素用共通スタイル定義。
 * @param {object} theme テーマ
 * @return {function}
 */
export const commonStyles = (theme) => ({
  button: {
    marginTop: '8px',
  },
  textField: {
    width: '70%',
    margin: '10px 10px 10px 10px',
  },
  typography: {
    textAlign: 'left',
    width: '70%',
    margin: 'auto',
  },
});

/**
 * ログイン画面 テキストフィールドの描画。
 * @param {object} param0 描画オプション
 * @return {JSX.Element} テキストフィールド
 */
export const renderTextField = ({
  input,
  label,
  meta: {touched, error, valid},
  ...custom
}) => (
  <TextField
    variant="standard"
    size={'small'}
    label={label}
    helperText={touched && error}
    error={touched && !valid}
    InputProps={{
      min: 1,
    }}
    {...input}
    {...custom} />
);


/**
 * ログイン画面系列 必須チェックの手動実行
 *
 * ログイン画面系の画面で、バリデーションが動作しない場合があるため
 * 本メソッドで暫定的にチェックを行いチェックエラーとする。
 *
 * 項目に入力を行うとバリデーションが動作するようになるため、
 * 必須チェックのみ行う。
 *
 * @param {function} createCheckTargets 手動で未入力チェックを行う入力項目情報を生成するメソッド
 * @param {function} showMessage メッセージ表示関数
 * @return {boolean} チェック結果
 */
export const checkInputEmpty = (createCheckTargets, showMessage) => {
  const checkTargets = createCheckTargets();

  for (const checkTarget of checkTargets) {
    // 必須チェックエラーの場合はメッセージを表示
    if (validateRule.required(checkTarget.value)) {
      showMessage({
        message: {
          id: 'TKSCS00005E',
          values: [checkTarget.name, ''],
        },
      });

      return false;
    }
  }

  return true;
};

/**
 * ログイン画面 共通部分の描画。
 * @param {object} children 子要素
 * @param {string} pageTitle ページ名
 * @return {JSX.Element} ログイン画面共通部分
 */
export default function Container({children, pageTitle}) {
  const classes = useStyles();
  const browserTitle = `${appConfig.sysTitle} ${pageTitle}`;
  return (
    <div>
      <Helmet>
        <title>{browserTitle}</title>
      </Helmet>
      <form noValidate autoComplete="off" className={classes.root}>
        <div className={classes.box}>
          <div style={{height: '10px'}}></div>
          <Box className={classes.title} boxShadow={3}>
            {appConfig.sysTitle}
          </Box>
          {children}
          <div style={{height: '10px'}}></div>
        </div>
      </form>
    </div>
  );
}

Container.propTypes = {
  children: PropTypes.object,
  pageTitle: PropTypes.string,
};
